import { useRef, type ReactNode } from 'react'
import { Search, X } from 'lucide-react'

import { Button } from '@/ui/button'
import { Input } from '@/ui/input'
import { useAssistantBrowserCommand } from '@/lib/assistant-browser-commands'
import { assistantBrowserCommands } from '@/lib/assistant-browser-commands.generated'

type SearchToolbarProps =
  | {
      value: string
      onChange: (value: string) => void
      label: string
      placeholder: string
      assistantFilter: string
      maxLength?: number
      children?: ReactNode
    }
  | {
      children: ReactNode
      value?: never
      onChange?: never
      label?: never
      placeholder?: never
      assistantFilter?: never
      maxLength?: never
    }

export function SearchToolbar({
  value,
  onChange,
  label,
  placeholder,
  assistantFilter,
  maxLength,
  children,
}: SearchToolbarProps) {
  const inputRef = useRef<HTMLInputElement>(null)

  useAssistantBrowserCommand(
    assistantBrowserCommands.filter,
    ({ value: next }) => {
      if (!onChange) return false
      onChange(next)
      inputRef.current?.focus()
      return true
    },
    ({ surface }) => assistantFilter !== undefined && surface === assistantFilter,
  )

  function clearSearch() {
    onChange?.('')
    inputRef.current?.focus()
  }

  return (
    <div
      data-slot="search-toolbar"
      className="border-border @container/toolbar flex flex-wrap items-center gap-2 border-b px-4 py-1"
    >
      {value !== undefined ? (
        <div className="relative min-w-48 flex-1 sm:max-w-sm">
          <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-1.5 size-3 -translate-y-1/2" />
          <Input
            ref={inputRef}
            data-assistant-filter={assistantFilter}
            value={value}
            onChange={(event) => onChange(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Escape' && value.length > 0) {
                event.preventDefault()
                clearSearch()
              }
            }}
            placeholder={placeholder}
            aria-label={label}
            maxLength={maxLength}
            className="h-6 w-full border-0 bg-transparent px-6 text-xs shadow-none outline-none placeholder:text-muted-foreground dark:bg-transparent"
          />
          {value.length > 0 ? (
            <Button
              type="button"
              variant="ghost"
              size="icon-xs"
              aria-label={`Clear ${label.toLowerCase()}`}
              onClick={clearSearch}
              className="text-muted-foreground hover:text-foreground absolute inset-y-0 right-1 my-auto"
            >
              <X aria-hidden="true" className="size-3" />
            </Button>
          ) : null}
        </div>
      ) : null}
      {children ? (
        <div className={value === undefined ? 'flex flex-wrap items-center gap-1' : 'ml-auto flex flex-wrap items-center gap-1'}>
          {children}
        </div>
      ) : null}
    </div>
  )
}
